import { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

// El cuerpo del anuncio es texto plano (no HTML, a propósito: lo escribe gente y no
// queremos inyección). Pero un anuncio del tipo "hay versión nueva, bajala de acá"
// sirve de poco si la URL no se puede tocar, así que convertimos las URLs http(s)
// en links. Sólo esas: el resto sigue siendo texto que React escapa solo, y al
// aceptar únicamente http/https no hay forma de colar un javascript:.
// OJO: el regex de split lleva /g, y .test() sobre un regex global es stateful
// (avanza lastIndex y alterna resultados). Por eso el chequeo usa uno propio SIN /g.
const URL_SPLIT_RE = /(https?:\/\/[^\s<>"')]+)/g;
const IS_URL_RE = /^https?:\/\//i;
function linkify(text) {
  return String(text || '').split(URL_SPLIT_RE).map((part, i) =>
    IS_URL_RE.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           style={{ color: 'var(--c-brand)', fontWeight: 600, wordBreak: 'break-all' }}>{part}</a>
      : part
  );
}

// Auto-shows on Dashboard if there's an unread announcement targeted at the
// current user. One at a time — dismiss to see the next. Reads come from
// /api/announcements/pending; dismissal POSTs to /api/announcements/:id/read.
export default function AnnouncementModal() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return;
    api.get('/announcements/pending')
      .then(r => setPending(r.data?.announcements || []))
      .catch(() => {});
  }, [user]);

  // Stamp when the currently-shown announcement appeared, so we can report how
  // long it was open when dismissed (engagement: "closed instantly" vs read).
  const shownAtRef = useRef(Date.now());
  useEffect(() => { shownAtRef.current = Date.now(); }, [pending[0]?.id]);

  if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return null;
  if (pending.length === 0) return null;

  const current = pending[0];
  const lang = i18n.language;
  const title = lang === 'en' ? current.title_en : current.title_es;
  const body  = lang === 'en' ? current.body_en  : current.body_es;

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/announcements/${current.id}/read`, { dwell_ms: Date.now() - shownAtRef.current });
      try {
        await api.post('/gamification/event', { type: 'announcement_read', metadata: { id: current.id, kind: current.kind } });
      } catch { /* ignore */ }
      const wasWelcome = typeof current.kind === 'string' && current.kind.startsWith('welcome');
      const remaining = pending.slice(1);
      setPending(remaining);
      // If the dismissed announcement was a welcome and the user hasn't
      // picked a partner yet, walk them straight to the picker. Only fire
      // when this was the last queued announcement so we don't yank focus
      // away from the next pending modal.
      if (wasWelcome && remaining.length === 0 && !user?.pokemonChoice) {
        navigate('/game#pokemon-picker');
      }
    } catch {
      // leave it pending; user can try again
    } finally {
      setBusy(false);
    }
  };

  // Portal to document.body so ancestor transforms (in Layout/FloatingChat/etc)
  // don't break position:fixed centering. See CLAUDE.md "Modals" note.
  return ReactDOM.createPortal((
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div className="card" style={{
        maxWidth: 560, width: '100%',
        maxHeight: '85vh', overflowY: 'auto',
        margin: 0,
        position: 'relative',
        background:
          'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, transparent 60%), var(--c-surface)',
        border: '1px solid var(--c-border)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(251, 191, 36, 0.4)',
        animation: 'announcement-pop 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          aria-label={t('common.close')}
          style={{
            position: 'absolute', top: 8, right: 8,
            background: 'transparent', border: 'none',
            cursor: 'pointer', padding: 4,
            color: 'var(--c-text-muted)',
          }}
        >
          <X size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 36, lineHeight: 1 }}>{current.emoji || '📣'}</span>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--c-text)' }}>
            {title}
          </h2>
        </div>

        <div style={{
          fontSize: 14, lineHeight: 1.55, color: 'var(--c-text)',
          whiteSpace: 'pre-wrap',
          marginBottom: 16,
        }}>
          {linkify(body)}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {pending.length > 1 && (
            <span style={{ fontSize: 12, color: 'var(--c-text-muted)', alignSelf: 'center', marginRight: 'auto' }}>
              +{pending.length - 1} {t('announcements.morePending')}
            </span>
          )}
          <button className="btn btn-primary" onClick={dismiss} disabled={busy}>
            {t('announcements.gotIt')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
